// ---------------------------------------------------------------------------
// resolve-source.ts — turn a Track into something <audio> can load
// ---------------------------------------------------------------------------
//
// Deliberately async, for both kinds. A stock track's URL could be built
// synchronously, but a local one cannot: it needs a permission check and a disk
// read. Making the single entry point async means there is NO synchronous path
// from a track to a playable URL — so the existence check for a local file
// (requirement 6) cannot be skipped by a caller that did not know to make one.
//
// Object URLs are created here and must be revoked by the caller when it moves
// on. They are not created for the whole library, only for the track about to
// play: a blob URL pins the file's bytes in memory for as long as it lives, and a
// long shift through a large library would accumulate all of them.
// ---------------------------------------------------------------------------

import { getLocalTrack } from '@/core/lib/music-db';

import { verifyLocalTrack } from './local-verify';
import { type LocalTrackStatus, type Track, isStockTrack, stockStreamUrl } from './track';

export type SourceResult =
  | {
      ok: true;
      url: string;
      /** True when the caller must revokeObjectURL(url) after use. */
      isObjectUrl: boolean;
      /** Duration read off the file, when the stored value was stale. */
      correctedDurationSec?: number | null;
    }
  | { ok: false; reason: LocalTrackStatus };

/**
 * Resolve a track to a URL, verifying a local file still exists first.
 *
 * Returns `{ ok: false }` rather than throwing: an unplayable track is an
 * ordinary state the queue skips past, not an exception. Throwing would push
 * try/catch into every advance path and make "skip and carry on" the hard case
 * instead of the easy one.
 */
export async function resolveTrackSource(track: Track): Promise<SourceResult> {
  if (isStockTrack(track)) {
    return { ok: true, url: stockStreamUrl(track.id), isObjectUrl: false };
  }

  // The handle lives in IndexedDB, keyed by the same id the queue holds.
  let record;
  try {
    record = await getLocalTrack(track.id);
  } catch {
    // The database is unavailable — a private window, or storage blocked. The
    // file is not reachable, which for the player is the same as missing.
    return { ok: false, reason: 'missing' };
  }

  if (!record) {
    // The queue references a local track this device has never seen. Normal when
    // a playlist built elsewhere is opened here.
    return { ok: false, reason: 'missing' };
  }

  const verified = await verifyLocalTrack(record);
  if (verified.status !== 'ok' || !verified.file) {
    return { ok: false, reason: verified.status };
  }

  return {
    ok: true,
    url: URL.createObjectURL(verified.file),
    isObjectUrl: true,
    // When the bytes changed, the stored duration describes a different file.
    // Null tells the caller to take the duration from the element instead.
    correctedDurationSec: verified.changed ? null : record.durationSec,
  };
}

/** Release an object URL, ignoring a URL that was never one. */
export function releaseSource(url: string | null, isObjectUrl: boolean): void {
  if (!url || !isObjectUrl) return;
  try {
    URL.revokeObjectURL(url);
  } catch {
    // Already revoked, or no URL API. Either way there is nothing to free.
  }
}
